import { defineStore } from 'pinia';
import { ReferralBuilderService } from '@/services';
import { useOrganizationStore } from '@/store';
import { ReferralBuilderInterface, ReferralBuilderSettingsInterface } from '@/types';

export default defineStore('OrganizationReferralBuilderStore', {
  state: () => {
    return {
      organizationId: useOrganizationStore().organization.id,
      referralBuilderSettings: [] as ReferralBuilderSettingsInterface[],
      referralBuilderSettingsBase: [
        {
          label: 'School/Organization',
          id: 'organization',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'SSID',
          id: 'student_id',
          include: {
            disabled: false,
            enabled: false,
          },
          required: {
            disabled: false,
            enabled: false,
          },
        },
        {
          label: 'First Name',
          id: 'student_first_name',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Last Name',
          id: 'student_last_name',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'District/School/Campus/Building',
          id: 'building_campus',
          include: {
            disabled: false,
            enabled: false,
          },
          required: {
            disabled: false,
            enabled: false,
          },
        },
        {
          label: 'Grade',
          id: 'grade',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Reason',
          id: 'referral_reason_id',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Referrer Title/Relationship to Referral',
          id: 'referrer_title',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Referrer First Name',
          id: 'referrer_first_name',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Referrer Last Name',
          id: 'referrer_last_name',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Referrer Email',
          id: 'referrer_email',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Referrer Phone',
          id: 'referrer_phone',
          include: {
            disabled: true,
            enabled: true,
          },
          required: {
            disabled: true,
            enabled: true,
          },
        },
        {
          label: 'Referral Code',
          id: 'referral_code',
          include: {
            disabled: false,
            enabled: false,
          },
          required: {
            disabled: false,
            enabled: false,
          },
        },
        {
          label: 'Send Confirmation Email',
          id: 'send_email',
          include: {
            disabled: false,
            enabled: false,
          },
          required: {
            disabled: true,
            enabled: false,
          },
        },
      ] as ReferralBuilderSettingsInterface[],
      referralBuilderSettingsId: undefined as number | null | undefined,
      referralBuilderSettingsLoading: false,
      referralCode: undefined as string | null | undefined,
      referralCodeError: false,
      referralFormId: undefined as string | null | undefined,
      referralFormCodeModal: false,
      referralFormLinkModal: false,
      referralFormPreviewModal: false,
      source: '',
    };
  },

  getters: {
    payload: (state) => {
      return {
        organization_id: useOrganizationStore().organization.id,
        referral_settings: JSON.stringify(state.referralBuilderSettings),
        referral_code: state.referralCode,
      };
    },
  },

  actions: {
    onIncludeChange(id: string) {
      const settingIndex = this.referralBuilderSettings.findIndex((item) => item.id === id);
      const setting = this.referralBuilderSettings[settingIndex];

      if (!setting.include.enabled) setting.required.enabled = setting.include.enabled;

      if (setting.id === 'send_email' && setting.include.enabled) {
        const referrerEmailIndex = this.referralBuilderSettings.findIndex((item) => item.id === 'referrer_email');
        this.referralBuilderSettings[referrerEmailIndex].include.enabled = true;
        this.referralBuilderSettings[referrerEmailIndex].required.enabled = true;
      }

      this.clearReferralCode(setting);
      this.blockReferrerEmailOption(setting);
    },

    onRequiredChange(id: string) {
      const settingIndex = this.referralBuilderSettings.findIndex((item) => item.id === id);
      const setting = this.referralBuilderSettings[settingIndex];

      if (setting.required.enabled) setting.include.enabled = setting.required.enabled;

      this.blockReferrerEmailOption(setting);
    },

    blockReferrerEmailOption(setting: ReferralBuilderSettingsInterface) {
      if (setting.id === 'referrer_email') {
        const sendEmailIndex = this.referralBuilderSettings.findIndex((item) => item.id === 'send_email');

        if (this.referralBuilderSettings[sendEmailIndex].include.enabled) {
          if (!setting.include.enabled || !setting.required.enabled) {
            this.doToast('Referrer email is required when the send confirmation email is enabled.', 'Error', true);

            setting.include.enabled = true;
            setting.required.enabled = true;
          }
        }
      }
    },

    clearReferralCode(setting: ReferralBuilderSettingsInterface) {
      if (setting.id === 'referral_code') {
        const referralCodeIndex = this.referralBuilderSettings.findIndex((item) => item.id === 'referral_code');

        if (!this.referralBuilderSettings[referralCodeIndex].include.enabled) {
          this.referralCode = null;
        }
      }
    },

    onReferralFormCodeModal() {
      this.referralFormCodeModal = true;
    },

    onReferralFormLinkModal() {
      this.referralFormLinkModal = true;
    },

    onReferralFormPreviewModal() {
      this.referralFormPreviewModal = true;
    },

    createRandomString(length: number) {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789~!';
      let result = '';

      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    onGenerateReferralCode() {
      this.referralCode = this.createRandomString(20);
    },

    onReferralBuilderSave() {
      this.referralBuilderSettingsId ? this.updateReferralBuilderSettings() : this.createReferralBuilderSettings();
    },

    onReferralBuilderCancel() {
      this.getReferralBuilderSettings();
    },

    async getReferralBuilderSettings() {
      this.referralBuilderSettingsLoading = true;
      const response = await ReferralBuilderService.getReferralBuilderSettings(useOrganizationStore().organization.id);
      await this.defineReferralBuilderData(response.data.data);
      this.referralBuilderSettingsLoading = false;
    },

    async createReferralBuilderSettings() {
      await ReferralBuilderService.createReferralBuilderSettings(this.payload)
        .then((response) => {
          this.defineReferralBuilderData(response.data.data);
          this.doToast('Referral Builder Settings Saved', 'Success');
        })
        .catch((error) => this.defineReferralError(error));
    },

    async updateReferralBuilderSettings() {
      if (this.referralBuilderSettingsId) {
        await ReferralBuilderService.updateReferralBuilderSettings(this.payload, this.referralBuilderSettingsId)
          .then((response) => {
            this.defineReferralBuilderData(response.data.data);
            this.doToast('Referral Builder Settings Saved', 'Success');
          })
          .catch((error) => this.defineReferralError(error));
      }
    },

    async defineReferralBuilderData(data: ReferralBuilderInterface) {
      this.referralBuilderSettingsId = data?.id;
      this.referralCode = data?.referral_code;
      this.referralFormId = data?.referral_form_id;
      this.referralBuilderSettings = data?.referral_settings.length
        ? data.referral_settings
        : this.referralBuilderSettingsBase;
      this.referralCodeError = false;
    },

    // eslint-disable-next-line
    // @ts-ignore
    defineReferralError(error) {
      this.referralCodeError = Object.keys(error.data.errors).findIndex((item) => item === 'referral_code') > -1;
      this.doToast(error.data.message, 'Error', true);
    },
  },
});
